import {
  useContext,
  useEffect,
  useInsertionEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";
import type {
  EventHandler,
  EventNode,
  Mediator,
  TypedEvent,
} from "@/shared/lib/componentHierarchy";
import { ComponentHierarchyContext } from "@/shared/ui/component-hierarchy/context";

export function useHierarchyHandlerRef<Handler>(handler: Handler): {
  current: Handler;
} {
  const current = useRef(handler);
  // Preact compat aliases insertion effects to layout effects; its synchronous
  // insertion callbacks need the handler before that fallback effect runs.
  if (useInsertionEffect === useLayoutEffect) current.current = handler;
  useInsertionEffect(() => {
    current.current = handler;
  }, [handler]);
  return current;
}

export function useComponentNode(
  handler?: EventHandler<TypedEvent>,
): EventNode<TypedEvent> {
  const parent = useContext(ComponentHierarchyContext);
  if (!parent) {
    throw new Error("Component presenters must be mounted under ComponentRoot");
  }
  const latestHandler = useHierarchyHandlerRef(handler);
  const node = useMemo(() => {
    const child = parent.createChild();
    child.subscribe(
      (event, context) =>
        latestHandler.current?.(event, context) ?? "unhandled",
    );
    return child;
  }, [parent, latestHandler]);
  useHierarchyLifetime(node);
  return node;
}

export function useMediatorSnapshot<State, Event extends TypedEvent, Effect>(
  mediator: Mediator<State, Event, Effect>,
): State {
  return useSyncExternalStore(
    mediator.subscribe,
    mediator.getSnapshot,
    mediator.getSnapshot,
  );
}

export function useHierarchyLifetime(resource: { dispose: () => void }): void {
  const lifecycle = useMemo(() => ({ resource, generation: 0 }), [resource]);
  useEffect(() => {
    lifecycle.generation += 1;
    return (): void => {
      const generation = lifecycle.generation;
      // StrictMode の cleanup/setup では同じインスタンスを保持する。
      queueMicrotask(() => {
        if (lifecycle.generation === generation) lifecycle.resource.dispose();
      });
    };
  }, [lifecycle]);
}
